import React, { useState, useEffect } from 'react';
import { 
  Target, CheckCircle2, Clock, Loader2, TrendingUp, 
  ChevronLeft, ChevronRight, CalendarDays
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const STATUS_CONFIG = {
  not_started: { label: 'Por iniciar', color: 'bg-gray-500', bgLight: 'bg-gray-500/20' },
  in_progress: { label: 'En progreso', color: 'bg-[#E86A1F]', bgLight: 'bg-[rgba(232,106,31,0.15)]' },
  completed: { label: 'Completado', color: 'bg-green-500', bgLight: 'bg-green-500/20' }
};

export default function ObjectivesPortalTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadObjectives();
  }, [selectedMonth, selectedYear]);

  const loadObjectives = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('portal_token');
      const response = await axios.get(
        `${API_URL}/api/objectives/my-objectives?month=${selectedMonth}&year=${selectedYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(response.data);
    } catch (error) {
      console.error('Error loading objectives:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  const objectives = data?.objectives || [];
  const stats = data?.stats || { total: 0, completed: 0, average_progress: 0 };

  return (
    <div className="max-w-3xl mx-auto" data-testid="objectives-portal">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl mb-4">
          <Target className="w-8 h-8 text-[#1D1D1F]" />
        </div>
        <h2 className="text-2xl font-bold text-[#1D1D1F] mb-2">Objetivos del Mes</h2>
        <p className="text-[#AEAEB2]">
          Metas que estamos trabajando para tu negocio
        </p>
      </div>

      {/* Month Selector */}
      <div className="flex items-center justify-center gap-4 mb-8">
        <button
          onClick={goToPreviousMonth}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors text-[#AEAEB2] hover:text-[#1D1D1F]"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 text-[#1D1D1F] font-semibold">
          <CalendarDays className="w-5 h-5 text-orange-500" />
          <span>{MONTH_NAMES[selectedMonth - 1]} {selectedYear}</span>
        </div>
        <button
          onClick={goToNextMonth}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors text-[#AEAEB2] hover:text-[#1D1D1F]"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Stats Summary */}
      {objectives.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-8">
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-[#1D1D1F]">{stats.total}</p>
            <p className="text-sm text-[#AEAEB2]">Objetivos</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-green-400">{stats.completed}</p>
            <p className="text-sm text-[#AEAEB2]">Completados</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-orange-400">{stats.average_progress}%</p>
            <p className="text-sm text-[#AEAEB2]">Avance</p>
          </div>
        </div>
      )}

      {/* Objectives List */}
      {objectives.length > 0 ? (
        <div className="space-y-4">
          {objectives.map((objective) => {
            const statusConfig = STATUS_CONFIG[objective.status] || STATUS_CONFIG.not_started;
            
            return (
              <div
                key={objective.objective_id}
                className="bg-white/5 rounded-xl p-5 border border-white/10"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#1D1D1F] mb-1">
                      {objective.title}
                    </h3>
                    {objective.description && (
                      <p className="text-[#AEAEB2] text-sm">{objective.description}</p>
                    )}
                  </div>
                  <span className={`flex items-center gap-1.5 px-3 py-1 ${statusConfig.bgLight} text-[#1D1D1F]/90 rounded-full text-xs font-medium`}>
                    {objective.status === 'completed' ? (
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    ) : (
                      <Clock className="w-3.5 h-3.5" />
                    )}
                    {statusConfig.label}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-[#AEAEB2]">Progreso</span>
                    <span className="text-[#1D1D1F] font-semibold">{objective.progress}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-500 ${
                        objective.progress >= 100 ? 'bg-green-500' : 'bg-gradient-to-r from-orange-500 to-amber-500'
                      }`}
                      style={{ width: `${Math.min(objective.progress, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 bg-white/5 rounded-xl border border-white/10">
          <Target className="w-12 h-12 text-[#6E6E73] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">Sin objetivos este mes</h3>
          <p className="text-[#AEAEB2]">
            Los objetivos del mes se mostrarán aquí cuando el equipo los defina
          </p>
        </div>
      )}

      {/* Info */}
      {objectives.length > 0 && (
        <div className="mt-6 text-center text-sm text-[#AEAEB2]">
          <TrendingUp className="w-4 h-4 inline mr-1" />
          Los objetivos son actualizados periódicamente por tu equipo
        </div>
      )}
    </div>
  );
}
